import type {
  McpCredentialTypeType,
  UpsertMcpCredentialDto,
} from './mcp-servers.schema';

export type McpFormCredentialType = McpCredentialTypeType | 'OAUTH_DCR';

export type McpAuthMode = 'oauth' | 'manualCredentials';

export function resolveMcpDraftSyncOptions({
  mcpAuthMode,
}: {
  mcpAuthMode: McpAuthMode;
}): {
  deferAutoDiscovery?: true;
  discoveryMode?: 'MANUAL';
} {
  return mcpAuthMode === 'manualCredentials'
    ? { deferAutoDiscovery: true, discoveryMode: 'MANUAL' }
    : {};
}

export function resolveInitialMcpAuthMode({
  discoveryMode,
  hasConfiguredMcpCredential,
}: {
  discoveryMode?: string | null;
  hasConfiguredMcpCredential: boolean;
}): McpAuthMode {
  if (discoveryMode === 'MANUAL' && !hasConfiguredMcpCredential) {
    return 'manualCredentials';
  }
  return 'oauth';
}

export type BuildMcpCredentialInput = {
  credentialType: McpFormCredentialType;
  bearerToken: string;
  apiKeyHeader: string;
  apiKey: string;
  basicUsername: string;
  basicPassword: string;
  oauthAuthorizeUrl: string;
  oauthTokenUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScopes: string;
  discoveryUrl: string;
  isEditing?: boolean;
};

export function toMcpFormCredentialType(
  value: string | null | undefined,
): McpFormCredentialType {
  switch (value) {
    case 'BEARER':
    case 'API_KEY':
    case 'BASIC':
    case 'OAUTH':
    case 'OAUTH_DCR':
    case 'NONE':
      return value;
    default:
      return 'NONE';
  }
}

export function isMcpCredentialSubmittable({
  credentialType,
  credential,
  isEditing = false,
  originalCredentialType,
}: {
  credentialType: McpFormCredentialType;
  credential: UpsertMcpCredentialDto | null;
  isEditing?: boolean;
  originalCredentialType?: McpFormCredentialType | null;
}): boolean {
  if (credentialType === 'NONE' || credential) {
    return true;
  }

  return Boolean(
    isEditing &&
      originalCredentialType &&
      originalCredentialType === credentialType,
  );
}

export function buildMcpCredential({
  credentialType,
  bearerToken,
  apiKeyHeader,
  apiKey,
  basicUsername,
  basicPassword,
  oauthAuthorizeUrl,
  oauthTokenUrl,
  oauthClientId,
  oauthClientSecret,
  oauthScopes,
  discoveryUrl,
  isEditing = false,
}: BuildMcpCredentialInput): UpsertMcpCredentialDto | null {
  const scopes = oauthScopes.split(' ').filter(Boolean);

  if (credentialType === 'NONE') return { type: 'NONE' };
  if (credentialType === 'BEARER') {
    if (!bearerToken && !isEditing) return null;
    if (bearerToken) return { type: 'BEARER', token: bearerToken };
    return null;
  }
  if (credentialType === 'API_KEY') {
    if (!apiKey && !isEditing) return null;
    if (apiKey) {
      return {
        type: 'API_KEY',
        header: apiKeyHeader || 'X-API-Key',
        key: apiKey,
      };
    }
    return null;
  }
  if (credentialType === 'BASIC') {
    if (!basicPassword && !isEditing) return null;
    if (basicPassword) {
      return {
        type: 'BASIC',
        username: basicUsername,
        password: basicPassword,
      };
    }
    return null;
  }
  if (credentialType === 'OAUTH') {
    if (!oauthAuthorizeUrl || !oauthTokenUrl || !oauthClientId) return null;
    return {
      type: 'OAUTH',
      authorizeUrl: oauthAuthorizeUrl,
      tokenUrl: oauthTokenUrl,
      clientId: oauthClientId,
      clientSecret: oauthClientSecret || undefined,
      scopes,
    };
  }
  if (!discoveryUrl.trim() && !isEditing) {
    return null;
  }
  return {
    type: 'OAUTH_DCR',
    discoveryUrl: discoveryUrl.trim(),
    scopes,
  };
}
